import React, { useState, useContext, useEffect } from 'react';
import {
    Button, Paper, Stepper, Step, StepLabel,
    TextField, RadioGroup, Radio, FormControlLabel, FormControl,
    CircularProgress, Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UserProfileContext from '../context/userprofilecontext.js';
import { setupUserCustomer, setupOrgCustomer, storePaymentMethod } from '../api/billingSetupApi.js';
import IntuitPaymentCapture from '../components/ui/IntuitPaymentCapture.js';
import PricingDisclosure from '../components/ui/PricingDisclosure.js';
import { useCustomersForCurrentUser } from '../api/swrcurrentusercontext.js';
import { mutate } from 'swr';
import mutateClearCache from '../api/mutateclearcache.js';
import { PageHeader } from '../design-system';
import styles from './page-setup-billing.module.css';


// ──────────────────────────────────────────────────────────────────────
// Determine what the user can set up based on their current state
// ──────────────────────────────────────────────────────────────────────

function getSetupMode(userProfile, customers) {
    if (!userProfile) return { mode: 'loading' };

    const isAlreadyCustomerUser = userProfile.isCustomer;
    const hasOrgs = customers?.some(c => c.customerType === 'organization');

    if (!isAlreadyCustomerUser && !hasOrgs) {
        return { mode: 'full', showIndividual: true, showOrg: true };
    }

    if (isAlreadyCustomerUser && !hasOrgs) {
        return { mode: 'org_only', showIndividual: false, showOrg: true };
    }

    if (!isAlreadyCustomerUser && hasOrgs) {
        return { mode: 'full', showIndividual: true, showOrg: true };
    }

    return { mode: 'org_only', showIndividual: false, showOrg: true };
}


// ──────────────────────────────────────────────────────────────────────
// Step 1: Choose customer type
// ──────────────────────────────────────────────────────────────────────

function StepChooseType({ customerType, setCustomerType, onNext, setupMode }) {
    const { showIndividual, showOrg } = setupMode;

    return (
        <div>
            <h3 className={styles.stepTitle}>
                {showIndividual
                    ? 'How would you like to manage cases?'
                    : 'Create a New Organization'
                }
            </h3>
            <FormControl component="fieldset" sx={{ mt: 2 }}>
                <RadioGroup
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value)}
                >
                    {showIndividual && (
                        <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
                            <FormControlLabel
                                value="user"
                                control={<Radio />}
                                label={
                                    <div className={styles.radioLabel}>
                                        <p className={styles.radioLabelTitle}>
                                            Individual
                                        </p>
                                        <p className={styles.radioLabelDesc}>
                                            I want to manage cases personally. Billing will be in my name.
                                        </p>
                                    </div>
                                }
                            />
                        </Paper>
                    )}
                    {showOrg && (
                        <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
                            <FormControlLabel
                                value="org"
                                control={<Radio />}
                                label={
                                    <div className={styles.radioLabel}>
                                        <p className={styles.radioLabelTitle}>
                                            Organization
                                        </p>
                                        <p className={styles.radioLabelDesc}>
                                            I represent a firm or company. Cases and billing will be under the organization.
                                        </p>
                                    </div>
                                }
                            />
                        </Paper>
                    )}
                </RadioGroup>
            </FormControl>
            <div className={styles.buttonRow}>
                <Button
                    variant="contained"
                    onClick={onNext}
                    disabled={!customerType}
                >
                    Continue
                </Button>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Step 2: Account details
// ──────────────────────────────────────────────────────────────────────

function StepAccountDetails({ customerType, userProfile, orgDetails, setOrgDetails, onNext, onBack, isSubmitting, error }) {
    if (customerType === 'user') {
        return (
            <div>
                <h3 className={styles.stepTitle}>
                    Confirm Your Details
                </h3>
                <Paper className={styles.confirmDetails} sx={{ p: 3 }} elevation={1}>
                    <p className={styles.confirmName}><strong>Name:</strong> {userProfile?.currentUser?.firstName} {userProfile?.currentUser?.lastName}</p>
                    <p className={styles.confirmEmail}><strong>Email:</strong> {userProfile?.currentUser?.email}</p>
                </Paper>
                <p className={styles.confirmNote}>
                    This information will be used to create your billing account.
                </p>
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                <div className={styles.buttonRow}>
                    <Button onClick={onBack} disabled={isSubmitting}>Back</Button>
                    <Button variant="contained" onClick={onNext} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <CircularProgress size={20} color="inherit" />
                                Creating account...
                            </span>
                        ) : 'Continue'}
                    </Button>
                </div>
            </div>
        );
    }

    // Org customer
    return (
        <div>
            <h3 className={styles.stepTitle}>
                Organization Details
            </h3>
            <div className={styles.formFields}>
                <TextField
                    label="Organization Name"
                    value={orgDetails.orgName}
                    onChange={(e) => setOrgDetails({ ...orgDetails, orgName: e.target.value })}
                    required
                    fullWidth
                    disabled={isSubmitting}
                />
                <TextField
                    label="Billing Email"
                    value={orgDetails.orgEmail}
                    required
                    onChange={(e) => setOrgDetails({ ...orgDetails, orgEmail: e.target.value })}
                    helperText="Invoices and billing notifications will be sent to this email"
                    fullWidth
                    disabled={isSubmitting}
                />
                <TextField
                    label="Phone"
                    value={orgDetails.orgPhone}
                    onChange={(e) => setOrgDetails({ ...orgDetails, orgPhone: e.target.value })}
                    fullWidth
                    disabled={isSubmitting}
                />
            </div>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <div className={styles.buttonRow}>
                <Button onClick={onBack} disabled={isSubmitting}>Back</Button>
                <Button
                    variant="contained"
                    onClick={onNext}
                    disabled={!orgDetails.orgName.trim() || !orgDetails.orgEmail.trim() || isSubmitting}
                >
                    {isSubmitting ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <CircularProgress size={20} color="inherit" />
                            Creating account...
                        </span>
                    ) : 'Continue'}
                </Button>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Step 3: Payment method capture (PCI-compliant)
// ──────────────────────────────────────────────────────────────────────

function StepPaymentMethod({ setupResult, onPaymentComplete, onBack, isSubmitting, error }) {
    if (!setupResult?.qboCustomerId) {
        return (
            <div>
                <Alert severity="error">
                    Billing account was not created. Please go back and try again.
                </Alert>
                <Button onClick={onBack} sx={{ mt: 2 }}>Back</Button>
            </div>
        );
    }

    return (
        <div>
            <h3 className={styles.stepTitle}>
                Payment Method
            </h3>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <PricingDisclosure customerID={setupResult.customerID} />

            <IntuitPaymentCapture
                onTokenReceived={onPaymentComplete}
                isSubmitting={isSubmitting}
                error={null}
            />

            <div className={styles.buttonRow}>
                <Button onClick={onBack} disabled={isSubmitting}>Back</Button>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Step 4: Complete
// ──────────────────────────────────────────────────────────────────────

function StepComplete({ customerType }) {
    const navigate = useNavigate();

    return (
        <div>
            <Alert severity="success" sx={{ mb: 3 }}>
                Your account has been set up successfully!
            </Alert>
            <h3 className={styles.stepTitle}>
                {customerType === 'user' ? 'Your Account is Ready' : 'Organization Account is Ready'}
            </h3>
            <p className={styles.stepDesc}>
                You can now create and manage cases.
                {customerType === 'org' && ' You have been set up as the organization administrator.'}
            </p>
            <Button
                variant="contained"
                sx={{ mt: 3 }}
                onClick={() => {
                    mutate('CURRENTUSER_CONTEXT');
                    mutate('CUSTOMERS_FOR_CURRENT_USER');
                    mutateClearCache();
                    navigate('/');
                }}
            >
                Go to Dashboard
            </Button>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Main wizard
// ──────────────────────────────────────────────────────────────────────

export default function BillingSetupPage() {
    const { userProfile, isLoading: isProfileLoading } = useContext(UserProfileContext);
    const { data: customers, isLoading: isCustomersLoading } = useCustomersForCurrentUser();
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [customerType, setCustomerType] = useState('');
    const [orgDetails, setOrgDetails] = useState({ orgName: '', orgEmail: '', orgPhone: '' });
    const [setupResult, setSetupResult] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [lockedMode, setLockedMode] = useState(null);

    const isLoading = isProfileLoading || isCustomersLoading;

    // Compute the current mode from live data, but use locked mode once set
    const liveMode = getSetupMode(userProfile, customers);
    const setupMode = lockedMode || liveMode;

    // Lock org_only mode immediately so it doesn't shift if SWR refreshes
    useEffect(() => {
        if (!lockedMode && liveMode.mode === 'org_only') {
            setLockedMode(liveMode);
        }
    }, [liveMode, lockedMode]);

    // Effective customer type: locked for org_only, user-selected for full
    const effectiveCustomerType = setupMode.mode === 'org_only' ? 'org' : customerType;

    const effectiveSteps = setupMode.mode === 'org_only'
        ? ['Organization Details', 'Payment Method', 'Complete']
        : ['Choose Account Type', 'Account Details', 'Payment Method', 'Complete'];

    const handleNext = () => {
        // Lock the mode when user advances past step 0 in full mode
        if (!lockedMode && activeStep === 0 && setupMode.mode === 'full') {
            setLockedMode(setupMode);
        }
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setError(null);
        setActiveStep((prev) => prev - 1);
    };

    // Create QBO Customer
    const handleCreateCustomer = async () => {
        setIsSubmitting(true);
        setError(null);

        try {
            let result;

            if (effectiveCustomerType === 'user') {
                result = await setupUserCustomer();
            } else {
                result = await setupOrgCustomer({
                    orgName: orgDetails.orgName.trim(),
                    orgEmail: orgDetails.orgEmail.trim(),
                    orgPhone: orgDetails.orgPhone.trim() || null,
                });
            }

            setSetupResult(result);
            handleNext();
        } catch (e) {
            console.error('Customer setup failed:', e);
            setError(e.message || 'Failed to create account. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Payment tokenized by Intuit, now store it via our backend
    const handlePaymentToken = async (token, tokenType, recaptchaToken) => {
        setIsSubmitting(true);
        setError(null);

        try {
            await storePaymentMethod({
                customerID: setupResult.customerID,
                paymentToken: token,
                tokenType: tokenType,
                recaptchaToken: recaptchaToken,
                setAsDefault: true,
            });

            handleNext();
        } catch (e) {
            console.error('Payment storage failed:', e);
            let msg = 'Failed to save payment method. Please try again.';
            try {
                const resp = e._response || e.response;
                if (resp?.body) {
                    let body = resp.body;
                    if (typeof body === 'string') {
                        body = body.replace(/'/g, '"');
                        const parsed = JSON.parse(body);
                        msg = parsed.message || parsed.error || msg;
                    }
                }
            } catch (parseErr) {
                // ignore parse errors
            }
            setError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className={styles.loadingState}>
                <CircularProgress size={24} />
                <span>Loading...</span>
            </div>
        );
    }

    // ── Page title and description based on mode ──
    let pageTitle, pageDescription;
    if (setupMode.mode === 'org_only') {
        pageTitle = 'Add an Organization';
        pageDescription = 'Create a new organization with its own billing and case management.';
    } else {
        pageTitle = 'Set Up Your Account';
        pageDescription = 'Get started by choosing how you want to manage your cases and billing.';
    }

    // ── Render steps based on mode ──

    if (setupMode.mode === 'org_only') {
        // Simplified flow: Org Details → Payment → Complete
        return (
            <div className={styles.page}>
                <PageHeader title={pageTitle} subtitle={pageDescription} />

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {effectiveSteps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 0 && (
                    <StepAccountDetails
                        customerType="org"
                        userProfile={userProfile}
                        orgDetails={orgDetails}
                        setOrgDetails={setOrgDetails}
                        onNext={handleCreateCustomer}
                        onBack={() => navigate('/')}
                        isSubmitting={isSubmitting}
                        error={error}
                    />
                )}

                {activeStep === 1 && (
                    <StepPaymentMethod
                        setupResult={setupResult}
                        onPaymentComplete={handlePaymentToken}
                        onBack={handleBack}
                        isSubmitting={isSubmitting}
                        error={error}
                    />
                )}

                {activeStep === 2 && (
                    <StepComplete customerType="org" />
                )}
            </div>
        );
    }

    // Full flow: Choose Type → Details → Payment → Complete
    return (
        <div className={styles.page}>
            <PageHeader title={pageTitle} subtitle={pageDescription} />

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {effectiveSteps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {activeStep === 0 && (
                <StepChooseType
                    customerType={customerType}
                    setCustomerType={setCustomerType}
                    onNext={handleNext}
                    setupMode={setupMode}
                />
            )}

            {activeStep === 1 && (
                <StepAccountDetails
                    customerType={effectiveCustomerType}
                    userProfile={userProfile}
                    orgDetails={orgDetails}
                    setOrgDetails={setOrgDetails}
                    onNext={handleCreateCustomer}
                    onBack={handleBack}
                    isSubmitting={isSubmitting}
                    error={error}
                />
            )}

            {activeStep === 2 && (
                <StepPaymentMethod
                    setupResult={setupResult}
                    onPaymentComplete={handlePaymentToken}
                    onBack={handleBack}
                    isSubmitting={isSubmitting}
                    error={error}
                />
            )}

            {activeStep === 3 && (
                <StepComplete customerType={effectiveCustomerType} />
            )}
        </div>
    );
}
